import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import SiteFooter from "@/components/SiteFooter";
import { useSiteContent } from "@/lib/content";
import {
  Cpu, ShieldCheck, Wifi, MonitorSmartphone, Bug, PhoneCall,
  Sparkles, ArrowRight, Star, ChevronRight, PlayCircle, MessageSquare, CloudUpload, Gauge, Wrench, Printer
} from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import QuickRequestForm from "@/components/QuickRequestForm";
import WhatsAppButton from "@/components/WhatsAppButton";

// Static registry mapping service titles to a preferred icon + photo.
// Admins can rename freely — anything not matched falls back to the default.
const SERVICE_VISUALS = {
  "Software & OS Fixes": { icon: MonitorSmartphone, photo: "https://images.unsplash.com/photo-1517430816045-df4b7de11d1d?auto=format&fit=crop&w=600&q=80" },
  "Hardware Troubleshoot": { icon: Cpu, photo: "https://images.unsplash.com/photo-1517336714731-489689fd1ca8?auto=format&fit=crop&w=600&q=80" },
  "Virus & Malware": { icon: Bug, photo: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&w=600&q=80" },
  "Wi-Fi & Network": { icon: Wifi, photo: "https://images.unsplash.com/photo-1544197150-b99a580bb7a8?auto=format&fit=crop&w=600&q=80" },
  "Security Checkup": { icon: ShieldCheck, photo: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&w=600&q=80" },
  "Custom PC Setup": { icon: MonitorSmartphone, photo: "https://images.unsplash.com/photo-1587202372634-32705e3bf49c?auto=format&fit=crop&w=600&q=80" },
  "Cloud Backup Setup & Secure": { icon: CloudUpload, photo: "https://images.unsplash.com/photo-1544197150-b99a580bb7a8?auto=format&fit=crop&w=600&q=80" },
  "Speed Up & Optimize": { icon: Gauge, photo: "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=600&q=80" },
  "Printer Support": { icon: Printer, photo: "https://images.unsplash.com/photo-1612815154858-60aa4c59eaa6?auto=format&fit=crop&w=600&q=80" },
};
const DEFAULT_VISUAL = { icon: Wrench, photo: "https://images.unsplash.com/photo-1517336714731-489689fd1ca8?auto=format&fit=crop&w=600&q=80" };

const PRESS = [
  "Now serving Dwarka, Delhi",
  "Trusted by home users & families",
  "Real support, honest pricing",
];

const FAQS = [
  { q: "What is Yonsii Geek Support?", a: "Yonsii connects you with verified tech geeks who call you at a slot you pick and fix your issue over the phone." },
  { q: "How much does a session cost?", a: "Standard sessions are ₹599 flat. Cloud Backup Setup is ₹799. Complex repairs start at ₹599, quoted on assessment. You only pay when a session is completed." },
  { q: "How do I book a fix?", a: "Sign in, tap Schedule new session, pick a date and time, drop your phone number and describe the issue. That's it." },
  { q: "What if my issue isn't fixed?", a: "If the technician can't resolve it, the session isn't marked completed and you're not charged. Every fix is logged with notes so you keep the receipts." },
];

export default function Landing() {
  const { content } = useSiteContent();
  const hero = content?.hero || {};
  const services = (content?.services && content.services.length > 0) ? content.services : [];
  const testimonials = content?.testimonials || [];

  const scrollerRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);

  const scrollByCards = (direction) => {
    const el = scrollerRef.current;
    if (!el) return;
    const firstCard = el.querySelector("[data-service-card]");
    const step = firstCard ? firstCard.offsetWidth + 20 : 300;
    el.scrollBy({ left: direction * step, behavior: "smooth" });
  };

  const handleScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const firstCard = el.querySelector("[data-service-card]");
    const step = firstCard ? firstCard.offsetWidth + 20 : 300;
    setActiveIdx(Math.min(services.length - 1, Math.max(0, Math.round(el.scrollLeft / step))));
  };

  return (
    <div className="min-h-screen navy-glow flex flex-col">
      <Navbar variant="public" />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 pt-14 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="chip mb-6">
              <span className="w-2 h-2 rounded-full bg-brand pulse-dot" />
              {hero.trust_note || "Serving Dwarka, Delhi"}
            </div>
            <h1 data-testid="hero-title" className="text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[1.02] tracking-tight text-white">
              {hero.title || "Your on-call tech geeks."}
            </h1>
            <p data-testid="hero-tagline" className="mt-4 text-xl sm:text-2xl font-semibold text-brand-light tracking-tight">
              {hero.tagline || "Real support, honest pricing"}
            </p>
            <p data-testid="hero-subtitle" className="mt-5 text-lg text-slate-300 max-w-xl leading-relaxed">
              {hero.subtitle}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/register">
                <Button data-testid="hero-book-btn" className="rounded-full bg-brand hover:bg-brand-dark text-white font-semibold h-12 px-7 text-base shadow-btn">
                  {hero.primary_cta || "Book a fix · $49"} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/login">
                <Button data-testid="hero-login-btn" variant="outline" className="rounded-full border border-white/20 bg-white/5 text-white font-semibold h-12 px-6 text-base hover:bg-white/10 hover:text-white">
                  <PlayCircle className="w-4 h-4 mr-2 text-brand-light" /> I already have an account
                </Button>
              </Link>
            </div>

            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3">
              {[
                "Certified Technicians",
                "Same-day Service",
                "Satisfaction Guaranteed",
              ].map((t) => (
                <div key={t} data-testid={`trust-signal-${t.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`} className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-brand/15 border border-brand/40 text-brand flex items-center justify-center">
                    <svg viewBox="0 0 20 20" fill="none" className="w-3.5 h-3.5" aria-hidden="true"><path d="M4 10.5l3.5 3.5L16 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </span>
                  <span className="text-sm sm:text-base font-semibold text-white">{t}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Hero image card — white */}
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="soft-card overflow-hidden p-0">
                <img
                  src="/hero.png"
                  alt="Yonsii technician on a support call"
                  className="w-full h-96 object-cover"
                />
              </div>
              <div className="absolute -top-3 -left-3 bg-white shadow-md rounded-2xl px-3 py-2 flex items-center gap-2 rotate-[-4deg] text-slate-900">
                <Sparkles className="w-4 h-4 text-brand" />
                <span className="text-sm font-bold">Flat ₹599</span>
              </div>
              <div className="absolute -bottom-3 -right-3 bg-brand text-white shadow-md rounded-2xl px-3 py-2 flex items-center gap-2 rotate-[3deg]">
                <PhoneCall className="w-4 h-4" />
                <span className="text-sm font-bold">We call you.</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRESS STRIP */}
      <section aria-label="Press" className="bg-[#06111F] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center gap-8 overflow-hidden">
          <span className="text-xs font-bold uppercase tracking-[1px] text-white/40 shrink-0">In the spotlight</span>
          <div className="overflow-hidden flex-1">
            <div className="marquee-track whitespace-nowrap">
              {[...PRESS, ...PRESS].map((p, i) => (
                <span key={i} className="text-white/70 text-sm font-semibold tracking-wide">{p}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section>
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight">
              One <span className="text-brand">geek</span> for every fix.
            </h2>
            <p className="text-slate-400 mt-3 text-base">Trained, verified technicians who solve the issues most support desks avoid.</p>
          </div>
          <div className="relative">
            <button
              type="button"
              data-testid="services-scroll-left"
              onClick={() => scrollByCards(-1)}
              aria-label="Scroll services left"
              className="hidden md:flex items-center justify-center absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-12 h-12 rounded-full bg-white shadow-lg border border-slate-200 text-slate-900 hover:bg-slate-50 transition"
            >
              <ChevronRight className="w-5 h-5 rotate-180" strokeWidth={2.5} />
            </button>
            <button
              type="button"
              data-testid="services-scroll-right"
              onClick={() => scrollByCards(1)}
              aria-label="Scroll services right"
              className="hidden md:flex items-center justify-center absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-12 h-12 rounded-full bg-white shadow-lg border border-slate-200 text-slate-900 hover:bg-slate-50 transition"
            >
              <ChevronRight className="w-5 h-5" strokeWidth={2.5} />
            </button>
            <div
              ref={scrollerRef}
              onScroll={handleScroll}
              data-testid="services-scroller"
              className="flex gap-5 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-4 -mx-6 px-6 md:mx-0 md:px-0"
              style={{ scrollbarWidth: "none" }}
            >
              {services.map((c) => {
                const visual = SERVICE_VISUALS[c.title] || DEFAULT_VISUAL;
                const Icon = visual.icon;
                return (
                  <Link
                    key={c.title}
                    to="/register"
                    data-service-card
                    data-testid={`service-tile-${c.title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                    className="group shrink-0 w-[280px] snap-start"
                  >
                    <div className="soft-card p-0 overflow-hidden h-full">
                      <div className="relative aspect-[264/300] bg-slate-100">
                        <img src={visual.photo} alt={c.title} className="absolute inset-0 w-full h-full object-cover opacity-95 group-hover:scale-[1.03] transition-transform duration-300" />
                        <div className="absolute top-3 left-3 bg-white/95 backdrop-blur rounded-full w-10 h-10 flex items-center justify-center shadow-sm">
                          <Icon className="w-5 h-5 text-brand" strokeWidth={2.2} />
                        </div>
                      </div>
                      <div className="p-4">
                        <div className="font-display font-bold text-slate-900">{c.title}</div>
                        <div className="text-xs text-slate-500 mt-1 line-clamp-2">{c.description}</div>
                        {c.price_label && (
                          <div className="mt-2 inline-flex items-center text-[11px] font-bold uppercase tracking-wider text-blue-700 bg-blue-50 border border-blue-100 rounded-full px-2 py-0.5">
                            Price: {c.price_label}
                          </div>
                        )}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
          <div data-testid="services-scroll-dots" className="mt-4 flex md:hidden items-center justify-center gap-2" aria-label="Services scroll indicator">
            {services.map((s, i) => (
              <span
                key={s.title}
                data-testid={`services-scroll-dot-${i}`}
                className={`h-2 rounded-full transition-all duration-200 ${i === activeIdx ? "w-6 bg-brand" : "w-2 bg-slate-500/50"}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section>
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight">
              How it <span className="text-brand">works</span>
            </h2>
            <p className="text-slate-400 mt-3">From booking to a working device in three simple steps.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { n: "01", t: "Book a Session", d: "Choose your service and pick a date and time that works for you." },
              { n: "02", t: "We Connect & Fix", d: "Our certified technician connects with you remotely or in person and resolves your issue." },
              { n: "03", t: "You're Good to Go", d: "We make sure everything works perfectly before we sign off. 100% satisfaction guaranteed." },
            ].map((s) => (
              <div key={s.n} data-testid={`how-step-${s.n}`} className="soft-card p-8 relative overflow-hidden">
                <span className="absolute -top-2 -right-2 text-8xl font-black text-blue-50 leading-none select-none">{s.n}</span>
                <div className="relative">
                  <div className="w-12 h-12 rounded-2xl bg-brand text-white flex items-center justify-center font-bold shadow-btn">{s.n}</div>
                  <div className="font-display font-bold text-2xl mt-5 text-slate-900">{s.t}</div>
                  <div className="text-slate-500 mt-2 leading-relaxed">{s.d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section>
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight">
                What our <span className="text-brand">customers say</span>
              </h2>
              <p className="text-slate-400 mt-2">Real people. Real fixes. Real relief.</p>
            </div>
            <div className="chip">
              <Star className="w-4 h-4 fill-current" /> Satisfaction guaranteed
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div key={i} data-testid={`testimonial-card-${i}`} className="soft-card p-6">
                <div className="flex items-center gap-1 mb-4">
                  {Array.from({ length: 5 }).map((_, k) => <Star key={k} className="w-4 h-4 fill-current text-brand" />)}
                </div>
                <p className="text-slate-800 text-lg font-medium leading-snug">“{t.quote}”</p>
                <div className="mt-5 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold">{t.name[0]}</div>
                  <div>
                    <div className="font-semibold text-slate-900">{t.name}</div>
                    <div className="text-xs text-slate-500">{t.city}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <QuickRequestForm />

      {/* FAQ */}
      <section>
        <div className="max-w-3xl mx-auto px-6 py-20">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight">
              Frequently <span className="text-brand">asked questions</span>
            </h2>
          </div>
          <Accordion type="single" collapsible defaultValue="q-0" className="space-y-3" data-testid="faq-accordion">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`q-${i}`} className="soft-card px-6 border-none">
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-semibold text-slate-900 hover:no-underline py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section>
        <div className="max-w-7xl mx-auto px-6 pb-24">
          <div className="relative overflow-hidden rounded-[36px] bg-white p-10 md:p-14 border border-slate-100 shadow-2xl">
            <div className="absolute -top-16 -right-16 w-72 h-72 bg-brand rounded-full opacity-15 blur-3xl" />
            <div className="absolute -bottom-20 -left-16 w-72 h-72 bg-brand rounded-full opacity-10 blur-3xl" />
            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-8">
                <div className="chip-light">
                  <Star className="w-4 h-4 fill-current" /> Loved by families
                </div>
                <h3 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 leading-tight tracking-tight">
                  Stop wrestling with tech.<br className="hidden sm:block"/> Get a <span className="text-brand">geek on the line.</span>
                </h3>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link to="/register">
                    <Button data-testid="cta-register-btn" className="rounded-full bg-brand hover:bg-brand-dark text-white font-semibold h-12 px-6 shadow-btn">
                      Book my ₹599 session <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </div>
              </div>
              <div className="lg:col-span-4">
                <div className="rounded-3xl bg-slate-50 border border-slate-100 p-6">
                  <MessageSquare className="w-6 h-6 text-brand" />
                  <p className="mt-4 text-slate-700 text-base leading-relaxed">
                    “Yonsii is the first support I've paid for that ended with an actual fix and clean notes I can send to my dad.”
                  </p>
                  <div className="mt-4 text-sm font-semibold text-slate-900">— Abhishek Chaudhary, Dwarka</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />
      <WhatsAppButton />
    </div>
  );
}
